import Papa from "papaparse";
import { useLayoutEffect, useMemo, useState } from "react";

import { TableColumnType } from "@/components/@types";
import { Buttons } from "@/components/atoms/Buttons";
import { FileDropzone } from "@/components/molecules/FileDropzone";
import { HeaderIndicesType } from "@/components/organisms/CSV";
import { CSVColumnMatcher } from "@/components/organisms/CSV/CSVColumnMatcher";
import { NodeTitleLayout } from "@/components/organisms/Node/NodeTitleLayout";
import { StickyLayout } from "@/components/organisms/StickyLayout";
import { Mutations } from "@/constants";
import { useBackendMutation } from "@/hooks/useBackendMutation";
import { State } from "@/state";
import { CommandContextType } from "@/state/@types";
import { XIcon } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Typography } from "@/components/ui/typography";

import { Spinner } from "@/components/ui/spinner";

// A file put into a bank account is a statement of it, whatever it is: a
// CSV export is read by its columns, a PDF by the model. A CSV's columns
// are matched here so the user can say which is which; the backend owns
// ``_CSV_HEADER_ALIASES`` in ``backend/app/core/services/db/extraction.py``
// and matches them itself when ``columnMapping`` is absent or partial. The
// mirror below only seeds the matcher; if the two drift, the worst case is
// a slightly worse first impression and the user fixes it via dropdown.

export const COMMAND_ID = "upload-to-financial-account";

export type UploadToFinancialAccountContextType = {
  /** ``identifier`` of the ``FinancialAccountObject`` the file goes into. */
  financialAccountIdentifier: string;
  /** Display name of the account, shown in the command title. */
  accountName?: string | null;
};

// The columns a CSV's lines are read from. The values here must match
// the keys of ``_CSV_HEADER_ALIASES`` in
// ``backend/app/core/services/db/extraction.py``: the mapping the user
// makes names columns by them.
//
// ``CSVColumnMatcher`` only reads ``objectKey`` and ``label`` so the
// cast to ``TableColumnType[]`` is intentional — the other table
// metadata isn't meaningful in this context.
const EXPECTED_HEADERS = [
  { objectKey: "date", label: "Date (required)" },
  { objectKey: "description", label: "Description" },
  { objectKey: "amount", label: "Amount" },
  { objectKey: "debit", label: "Debit" },
  { objectKey: "credit", label: "Credit" },
  { objectKey: "currency", label: "Currency" },
  { objectKey: "merchant", label: "Merchant" },
  { objectKey: "category", label: "Category" },
] as unknown as TableColumnType[];

// Mirror of ``_CSV_HEADER_ALIASES`` in
// ``backend/app/core/services/db/extraction.py``. Kept
// here so we can pre-arrange the column matcher with a best-guess
// mapping the moment a file is dropped — the user only has to adjust
// what we couldn't auto-detect. If the backend list grows, update this
// to match (the upload itself will still succeed via the backend's own
// alias matching even if this map is stale; only the auto-mapping UX
// degrades).
const CSV_HEADER_ALIASES: Record<string, string[]> = {
  date: [
    "date",
    "transaction date",
    "posted date",
    "posting date",
    "post date",
  ],
  description: ["description", "memo", "details", "narration", "name", "payee"],
  amount: ["amount", "value"],
  debit: ["debit", "withdrawal", "withdrawals", "debits"],
  credit: ["credit", "deposit", "deposits", "credits"],
  currency: ["currency", "iso currency", "iso_currency_code"],
  merchant: ["merchant", "merchant name", "merchant_name"],
  category: ["category"],
};

function normalizeHeader(raw: string): string {
  return raw.trim().toLowerCase().replace(/_/g, " ");
}

/**
 * Build a seed mapping + matcher ordering by fuzzy-matching the user's
 * CSV headers against the backend's known alias list. Each file column
 * is matched at most once; unmatched file columns are pushed to the
 * tail of the order so the user can drag them in if they really do
 * correspond to one of the expected fields under an unusual name.
 */
function autoMatchHeaders(
  fileHeaders: string[],
  expectedHeaders: TableColumnType[],
): { initialOrder: string[]; initialIndices: HeaderIndicesType } {
  const usedFileIdx = new Set<number>();
  const initialOrder: string[] = [];
  const initialIndices: HeaderIndicesType = {};

  expectedHeaders.forEach((expected, expectedIdx) => {
    const aliases = CSV_HEADER_ALIASES[expected.objectKey] ?? [
      expected.objectKey,
    ];
    const matchedFileIdx = fileHeaders.findIndex(
      (h, i) =>
        !usedFileIdx.has(i) && aliases.includes(normalizeHeader(h ?? "")),
    );
    if (matchedFileIdx >= 0) {
      usedFileIdx.add(matchedFileIdx);
      initialOrder.push(fileHeaders[matchedFileIdx]);
      initialIndices[expected.objectKey] = matchedFileIdx;
    } else {
      // Placeholder slot — same sentinel shape ``CSVColumnMatcher`` uses
      // for "no match yet" so its drag handlers treat it consistently.
      initialOrder.push(`No match-${expectedIdx}`);
      initialIndices[expected.objectKey] = -1;
    }
  });

  fileHeaders.forEach((h, i) => {
    if (!usedFileIdx.has(i)) {
      initialOrder.push(h);
    }
  });

  return { initialOrder, initialIndices };
}

/**
 * Strip placeholder / negative entries so the wire format only carries
 * canonical-key → column-index pairs the backend will act on. The
 * backend tolerates extra/unknown keys, but trimming here keeps the
 * mutation payload honest in tooling like the GraphQL inspector.
 */
function toWireMapping(
  headerIndices: HeaderIndicesType,
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [key, idx] of Object.entries(headerIndices)) {
    if (typeof idx === "number" && idx >= 0) {
      out[key] = idx;
    }
  }
  return out;
}

function Component({
  context,
}: {
  commandInstanceId: string;
  commandDefinitionId: string;
  context?: CommandContextType;
}) {
  // ``CommandDefinitionType.component`` is typed for the union of *all*
  // command contexts because the dispatcher doesn't know which command
  // it's invoking. Narrow once at the top so the body can rely on the
  // shape we actually pushed onto the stack.
  const uploadContext = context as
    | UploadToFinancialAccountContextType
    | undefined;
  const setCommandSearchValue = State((store) => store.setCommandSearchValue);
  const clearCommandStack = State((store) => store.clearCommandStack);
  const setSnackbarMessage = State((store) => store.setSnackbarMessage);

  const [file, setFile] = useState<File | null>(null);
  const [parsedRows, setParsedRows] = useState<string[][] | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [headerIndices, setHeaderIndices] = useState<HeaderIndicesType>({});
  // Seed for ``CSVColumnMatcher``'s internal item ordering. We compute
  // this once per file (when ``parsedRows`` is set) so the matcher
  // mounts already pre-arranged; it's not used after that.
  const [initialMatcherOrder, setInitialMatcherOrder] = useState<
    string[] | null
  >(null);

  const fileHeaders = parsedRows?.[0] ?? [];
  // Only a CSV has columns to match: any other file is read as it is. Asked
  // of the file in hand, and of one as it is dropped, before it is in hand.
  const isCsvFile = (candidate: File | null) =>
    !!candidate &&
    (candidate.type === "text/csv" ||
      candidate.name.toLowerCase().endsWith(".csv"));
  const isCsv = isCsvFile(file);

  // A CSV's lines need a date column and one of amount / debit / credit
  // (see ``Extractor.extract_from_file``). Mirror that here so the user
  // can't hit Upload with an unusable mapping.
  const isMappingValid = useMemo(() => {
    const has = (k: string) =>
      headerIndices[k] != null && (headerIndices[k] as number) >= 0;
    return has("date") && (has("amount") || has("debit") || has("credit"));
  }, [headerIndices]);

  const resetUploadState = () => {
    setFile(null);
    setParsedRows(null);
    setHeaderIndices({});
    setInitialMatcherOrder(null);
    setParseError(null);
  };

  const { mutate, loading } = useBackendMutation({
    mutation: Mutations.INGEST_FILE,
    variables: {
      input: {
        financialAccountIdentifier: uploadContext?.financialAccountIdentifier,
        // Placeholder — the real file + mapping are supplied via the
        // ``variables`` override passed to ``mutate`` below.
        file: null,
        columnMapping: null,
      },
    },
    callbacks: {
      onSuccess: () => {
        setSnackbarMessage(
          "Uploaded — its transactions will appear once it has been read.",
        );
        resetUploadState();
        setCommandSearchValue("");
        clearCommandStack();
      },
    },
  });

  const onDrop = (droppedFiles: File[]) => {
    const dropped = droppedFiles[0];
    if (!dropped) return;
    setParseError(null);
    setHeaderIndices({});
    setInitialMatcherOrder(null);
    setParsedRows(null);
    setFile(dropped);
    if (!isCsvFile(dropped)) return;
    Papa.parse<string[]>(dropped, {
      skipEmptyLines: true,
      complete: (result) => {
        const data = (result.data ?? []) as string[][];
        if ((result.errors?.length ?? 0) > 0 || data.length < 2) {
          setParseError(
            "Unable to read this CSV. Please check the file and try again.",
          );
          return;
        }
        const headers = (data[0] ?? []).map((h) => String(h ?? ""));
        const { initialOrder, initialIndices } = autoMatchHeaders(
          headers,
          EXPECTED_HEADERS,
        );
        // Seed both pieces of state *before* the matcher mounts so its
        // initial render shows the auto-detected mapping rather than a
        // flash of the legacy positional default.
        setInitialMatcherOrder(initialOrder);
        setHeaderIndices(initialIndices);
        setParsedRows(data);
      },
      error: () => {
        setParseError(
          "Unable to read this CSV. Please check the file and try again.",
        );
      },
    });
  };

  const onUpload = () => {
    if (!file || (isCsv && !parsedRows)) return;
    mutate({
      variables: {
        input: {
          financialAccountIdentifier: uploadContext?.financialAccountIdentifier,
          // Ship the user's *original* file untouched so the audit
          // trail (``Document.file``) preserves what they actually
          // submitted. A CSV's mapping is sent alongside it and applied
          // server-side; without one the backend matches its headers
          // by the names banks give them.
          file,
          columnMapping: isCsv ? toWireMapping(headerIndices) : null,
        },
      },
    });
  };

  return (
    <StickyLayout
      stickyTopChildren={
        <NodeTitleLayout
          title={
            uploadContext?.accountName
              ? `Upload transactions — ${uploadContext.accountName}`
              : "Upload transactions"
          }
          store={null}
        />
      }
      stickyBottomChildren={
        <Buttons
          buttons={[
            {
              variant: "outline",
              children: "Cancel",

              onClick: () => {
                clearCommandStack();
              },

              disabled: loading,
            },
            {
              variant: "default",
              children: <>{loading && <Spinner />}Upload</>,
              onClick: onUpload,
              disabled:
                loading || !file || (isCsv && (!parsedRows || !isMappingValid)),
            },
          ]}
        />
      }
    >
      {!file && (
        <FileDropzone
          accept={{ "text/csv": [".csv"], "application/pdf": [".pdf"] }}
          multiple={false}
          hint="Drag and drop a CSV export or a PDF statement"
          onDrop={onDrop}
        />
      )}

      {file && (
        <div className="flex flex-row gap-2 p-2 pt-1 flex-wrap">
          <Badge
            variant="outline"
            key={file.name}
            className="px-3 py-1 text-sm"
          >
            {file.name}
            <button
              type="button"
              aria-label="Remove"
              onClick={() => {
                resetUploadState();
              }}
              className="-mr-1 rounded-full p-0.5 hover:bg-foreground/10"
            >
              <XIcon className="size-3" />
            </button>
          </Badge>
        </div>
      )}

      {parseError && (
        <Typography className="text-destructive pl-2 pr-2">
          {parseError}
        </Typography>
      )}

      {parsedRows && (
        <div className="pl-2 pr-2 pb-2">
          <Typography level="body-sm" className="text-muted-foreground/80 mb-2">
            Match the columns in your CSV to the expected fields. Drag the CSV
            column rows on the right to line them up with the fields on the
            left, or click Remove to skip a column. A Date column plus one of
            Amount, Debit, or Credit are required.
          </Typography>
          <CSVColumnMatcher
            expectedHeaders={EXPECTED_HEADERS}
            fileHeaders={fileHeaders}
            headerIndicesState={[headerIndices, setHeaderIndices]}
            initialOrder={initialMatcherOrder ?? undefined}
          />
        </div>
      )}
    </StickyLayout>
  );
}

export function UploadToFinancialAccountCommand() {
  const registerCommandDefinition = State(
    (state) => state.registerCommandDefinition,
  );
  const unregisterCommandDefinition = State(
    (state) => state.unregisterCommandDefinition,
  );

  useLayoutEffect(() => {
    registerCommandDefinition({
      id: COMMAND_ID,
      title: "Upload transactions",
      description:
        "Upload a CSV export or a PDF statement into a financial account",
      component: Component,
      group: "Files",
      // Without an explicit ``getLabel`` the breadcrumb falls back to
      // formatting the auto-generated ``crypto.randomUUID()``
      // ``commandInstanceId`` (see ``CommandCenterTopbar``), which renders
      // as a stray "D9f2193c dd21 4f40 ..." above the dialog title.
      getLabel: () => "Upload transactions",
    });

    return () => {
      unregisterCommandDefinition(COMMAND_ID);
    };
  }, []);

  return null;
}
